import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus, Trash, PencilSimple, FloppyDisk, UploadSimple } from '@phosphor-icons/react';
import { API } from './adminConstants';

export function TestimonialManagement() {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', company: '', text: '', image_url: '' });
  const [uploading, setUploading] = useState(false);

  const load = async () => { try { const r = await axios.get(`${API}/testimonials`); setItems(r.data); } catch { toast.error('Failed to load'); } };
  useEffect(() => { load(); }, []);
  const resetForm = () => { setForm({ name: '', company: '', text: '', image_url: '' }); setEditItem(null); setShowForm(false); };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const formData = new FormData(); formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/testimonials/upload-image`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false); e.target.value = '';
  };

  const handleSave = async () => {
    if (!form.name || !form.text) { toast.error('Name and text required'); return; }
    try {
      if (editItem) { await axios.put(`${API}/admin/testimonials/${editItem.id}`, form, { headers: getAuthHeader() }); toast.success('Updated'); }
      else { await axios.post(`${API}/admin/testimonials`, form, { headers: getAuthHeader() }); toast.success('Created'); }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (item) => { setEditItem(item); setForm({ name: item.name || '', company: item.company || '', text: item.text || '', image_url: item.image_url || '' }); setShowForm(true); };
  const handleDelete = async (id) => { try { await axios.delete(`${API}/admin/testimonials/${id}`, { headers: getAuthHeader() }); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };

  return (
    <div data-testid="testimonial-management">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Testimonials</h1><p className="text-slate-500 text-sm">Manage customer testimonials shown on the website</p></div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-lime-600 hover:bg-lime-700" data-testid="create-testimonial-btn"><Plus size={15} className="mr-1" />Add Testimonial</Button>
      </div>
      {showForm && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="testimonial-form">
          <h3 className="font-semibold text-slate-900 mb-4">{editItem ? 'Edit Testimonial' : 'New Testimonial'}</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs">Customer Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. John Smith" data-testid="testimonial-name-input" /></div>
              <div><Label className="text-xs">Company / Role</Label><Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="e.g. Local Business Owner" data-testid="testimonial-company-input" /></div>
            </div>
            <div><Label className="text-xs">Testimonial Text *</Label><Textarea value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} placeholder="What did the customer say?" rows={4} data-testid="testimonial-text-input" /></div>
            <div><Label className="text-xs">Photo</Label>
              <div className="flex gap-3 items-start"><div className="flex-1"><Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="Paste URL or upload..." /></div>
                <label className="cursor-pointer shrink-0"><Button variant="outline" size="sm" asChild disabled={uploading}><span><UploadSimple size={15} className="mr-1" />{uploading ? 'Uploading...' : 'Upload Photo'}</span></Button><input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="testimonial-image-upload" /></label>
              </div>
              {form.image_url && <div className="mt-2 w-16 h-16 rounded-full overflow-hidden border border-slate-200"><img src={form.image_url} alt="Preview" className="w-full h-full object-cover" /></div>}
            </div>
          </div>
          <div className="flex gap-3 mt-5"><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSave} className="bg-lime-600 hover:bg-lime-700" data-testid="save-testimonial-btn"><FloppyDisk size={15} className="mr-1" />{editItem ? 'Update' : 'Create'}</Button></div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="divide-y divide-slate-100">
          {items.map(item => (
            <div key={item.id} className="p-4 flex items-center gap-4" data-testid={`testimonial-item-${item.id}`}>
              {item.image_url && <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 border border-slate-200"><img src={item.image_url} alt="" className="w-full h-full object-cover" /></div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><p className="font-medium text-slate-900">{item.name}</p>{item.company && <span className="text-xs text-slate-500">- {item.company}</span>}</div>
                <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">&ldquo;{item.text}&rdquo;</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => startEdit(item)}><PencilSimple size={14} className="mr-1" />Edit</Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 border-red-200 hover:bg-red-50"><Trash size={14} /></Button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="p-12 text-center text-slate-500">No testimonials yet. Add your first!</div>}
        </div>
      </div>
    </div>
  );
}
